let audioContext = null;
let unlocked = false;

/**
 * 自定义报警音：将 mp3 放到 public/alarm/custom.mp3
 * 访问路径为 /alarm/custom.mp3，存在则优先播放，否则使用内置 alarm
 */
const CUSTOM_ALARM_MP3_URL = `${process.env.BASE_URL || "/"}alarm/custom.mp3`;
const DEFAULT_PRESET_TYPE = "alarm";

/**
 * 内置音效（无 custom.mp3 时使用）:
 * - alarm  双音急促报警（默认）
 * - siren  警笛声
 * - urgent 高频三连响
 * - bell   钟声提醒
 */
export const ALARM_SOUND_TYPES = ["alarm", "siren", "urgent", "bell"];
let currentSoundType = DEFAULT_PRESET_TYPE;

let customMp3Probe = null;
let customMp3Available = false;
// 持久化 HTMLAudioElement, iOS/微信内必须靠同一个实例在用户手势里先 play 一次才能后续自动播放
let customAudioEl = null;
// 上一次真正报警开始的时间戳, 用于避免 unlock 的静音预热回读时误断真报警
let realPlayStartedAt = 0;
// 当前正在响的内置 preset 节点, 便于 stopAlarmSound() 强制提前静掉
let activeMaster = null;
let activeOscs = [];

function registerPresetOsc(osc) {
  activeOscs.push(osc);
  osc.onended = () => {
    const i = activeOscs.indexOf(osc);
    if (i >= 0) activeOscs.splice(i, 1);
    try { osc.disconnect(); } catch (e) {}
  };
}

export function setAlarmSoundType(type) {
  if (ALARM_SOUND_TYPES.includes(type)) {
    currentSoundType = type;
  }
}

export function getAlarmSoundType() {
  return currentSoundType;
}

export function getCustomAlarmMp3Url() {
  return CUSTOM_ALARM_MP3_URL;
}

function getAudioContext() {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) {
    return null;
  }
  if (!audioContext) {
    audioContext = new AudioCtx();
  }
  return audioContext;
}

/** 详细诊断日志, 同时写到屏幕角标, 手机上没控制台也能看到 */
function debugLog(...args) {
  const msg = args.map((a) => (typeof a === "object" ? JSON.stringify(a) : String(a))).join(" ");
  if (typeof window !== "undefined") {
    window.__alarmLastLog = msg;
    updateAlarmBadge(msg);
  }
  if (typeof window !== "undefined" && window.__alarmDebug) {
    console.log("[alarmSound]", ...args);
  }
}

/** 右下角小角标: 只在 URL 带 ?alarmDebug=1 或 __alarm.badge(true) 时显示 */
let badgeEl = null;
let badgeOn = false;
function ensureBadge() {
  if (badgeEl || typeof document === "undefined") return null;
  badgeEl = document.createElement("div");
  badgeEl.style.cssText =
    "position:fixed;right:10px;bottom:10px;z-index:99999;background:rgba(0,0,0,.75);" +
    "color:#0ff;font:12px/1.4 Consolas,monospace;padding:8px 10px;border-radius:6px;" +
    "max-width:60vw;word-break:break-all;pointer-events:none;";
  document.body.appendChild(badgeEl);
  return badgeEl;
}
function updateAlarmBadge(msg) {
  if (!badgeOn) return;
  const el = ensureBadge();
  if (!el) return;
  const t = new Date();
  const hh = String(t.getHours()).padStart(2, "0");
  const mm = String(t.getMinutes()).padStart(2, "0");
  const ss = String(t.getSeconds()).padStart(2, "0");
  el.textContent = `[${hh}:${mm}:${ss}] custom=${customMp3Available} ${msg}`;
}
export function setAlarmBadge(on) {
  badgeOn = !!on;
  if (typeof window !== "undefined") window.__alarmBadgeOn = badgeOn;
  if (!badgeOn && badgeEl) badgeEl.remove(), (badgeEl = null);
}

/** 检测 public/alarm/custom.mp3 是否存在: HEAD 请求 + 预热 <audio> 双保险 */
export function ensureCustomMp3Checked(force = false) {
  if (customMp3Probe && !force) {
    return customMp3Probe;
  }
  customMp3Probe = new Promise((resolve) => {
    let settled = false;
    const finish = (ok, why) => {
      if (settled) {
        return;
      }
      settled = true;
      customMp3Available = ok;
      if (typeof window !== "undefined") {
        window.__alarmCustomOk = ok;
      }
      debugLog("probe result", ok, "-", why, "url:", CUSTOM_ALARM_MP3_URL);
      resolve(ok);
    };
    debugLog("start HEAD probe...");
    // 1) 先 HEAD, 状态码 2xx/3xx 即视为存在; no-store 避免命中旧的 404 缓存
    fetch(CUSTOM_ALARM_MP3_URL, { method: "HEAD", cache: "no-store" })
      .then((res) => {
        debugLog("HEAD status", res.status, res.ok);
        if (res.ok) {
          preloadCustomMp3(finish);
        } else {
          finish(false, "HEAD not ok");
        }
      })
      .catch((err) => finish(false, "HEAD fetch error: " + err.message));
    // 2) 兜底超时
    setTimeout(() => finish(customMp3Available, "probe timeout 4s"), 4000);
  });
  return customMp3Probe;
}

function preloadCustomMp3(finish) {
  const audio = new Audio();
  const ok = (ev) => finish(true, "audio event: " + ev);
  audio.preload = "auto";
  ["canplay", "canplaythrough", "loadeddata", "loadedmetadata"].forEach((ev) => {
    audio.addEventListener(ev, () => ok(ev), { once: true });
  });
  audio.addEventListener("error", () => {
    const err = audio.error;
    finish(false, "audio error code=" + (err && err.code) + " msg=" + (err && err.message));
  }, { once: true });
  audio.src = CUSTOM_ALARM_MP3_URL;
  // HEAD 已经确认存在, 这里只是预热; 1200ms 没等到事件也直接判定可用
  setTimeout(() => finish(true, "warmup fallback (HEAD 已确认文件存在)"), 1200);
}

/** 获取/创建持久化的自定义 mp3 Audio 实例 */
function getCustomAudioEl() {
  if (!customAudioEl) {
    customAudioEl = new Audio();
    customAudioEl.preload = "auto";
    customAudioEl.src = CUSTOM_ALARM_MP3_URL;
    customAudioEl.volume = 1;
    customAudioEl.addEventListener("error", () => {
      debugLog("persistent audio error code=" + (customAudioEl.error && customAudioEl.error.code));
    });
  }
  return customAudioEl;
}

/** 浏览器要求用户交互后才能播放声音 */
export function unlockAlarmSound() {
  const ctx = getAudioContext();
  if (ctx && ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  unlocked = true;
  ensureCustomMp3Checked();
  // iOS Safari / 微信内: 必须在当前手势内对同一个 HTMLAudioElement 触发一次 play(), 哪怕静音,
  // 后续 setInterval 里对同一个实例的 play() 才不会被自动播放策略拦截
  const el = getCustomAudioEl();
  el.muted = true;
  const p = el.play();
  if (p && typeof p.catch === "function") {
    p.catch((err) => debugLog("iOS unlock fail:", err.name, err.message));
  }
  setTimeout(() => {
    // 如果这 300ms 内真实报警已经开始播放, 不要去 pause 打断
    if (realPlayStartedAt && Date.now() - realPlayStartedAt < 15000) {
      el.muted = false;
      return;
    }
    try {
      el.pause();
      el.currentTime = 0;
    } catch (e) {}
    el.muted = false;
  }, 300);
}

function createMasterGain(ctx, startAt, endAt, peakGain) {
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, startAt);
  master.gain.exponentialRampToValueAtTime(peakGain, startAt + 0.03);
  master.gain.setValueAtTime(peakGain, endAt - 0.08);
  master.gain.exponentialRampToValueAtTime(0.0001, endAt);
  master.connect(ctx.destination);
  activeMaster = master;
  return master;
}

function tone(ctx, startAt, duration, frequency, type, gainValue, master) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(frequency, startAt);
  gain.gain.setValueAtTime(gainValue, startAt);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
  osc.connect(gain);
  gain.connect(master);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.02);
  registerPresetOsc(osc);
}

function playAlarmPreset(ctx, now) {
  const peakGain = 0.95;
  const endAt = now + 5.2;
  const master = createMasterGain(ctx, now, endAt, peakGain);
  const beepDuration = 0.55;
  const gap = 0.72;
  const tones = [
    [980, 1180],
    [980, 1180],
    [880, 1040],
    [980, 1180],
    [880, 1040],
  ];
  tones.forEach(([low, high], index) => {
    const t = now + index * gap;
    tone(ctx, t, beepDuration, low, "square", 0.55, master);
    tone(ctx, t + 0.08, beepDuration - 0.08, high, "square", 0.45, master);
    tone(ctx, t, beepDuration, low * 0.5, "sine", 0.2, master);
  });
}

function playSirenPreset(ctx, now) {
  const peakGain = 0.9;
  const endAt = now + 6;
  const master = createMasterGain(ctx, now, endAt, peakGain);
  const cycles = 6;
  for (let i = 0; i < cycles; i += 1) {
    const t = now + i * 0.95;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(520, t);
    osc.frequency.linearRampToValueAtTime(920, t + 0.42);
    osc.frequency.linearRampToValueAtTime(520, t + 0.85);
    gain.gain.setValueAtTime(0.45, t);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.88);
    osc.connect(gain);
    gain.connect(master);
    osc.start(t);
    osc.stop(t + 0.9);
    registerPresetOsc(osc);
  }
}

function playUrgentPreset(ctx, now) {
  const peakGain = 0.95;
  const endAt = now + 4.5;
  const master = createMasterGain(ctx, now, endAt, peakGain);
  const bursts = [
    [1400, 1650, 1400],
    [1400, 1650, 1400],
    [1200, 1500, 1200],
  ];
  bursts.forEach((freqs, burstIndex) => {
    freqs.forEach((freq, i) => {
      const t = now + burstIndex * 1.35 + i * 0.22;
      tone(ctx, t, 0.28, freq, "square", 0.6, master);
      tone(ctx, t, 0.28, freq * 2, "sine", 0.15, master);
    });
  });
}

function playBellPreset(ctx, now) {
  const peakGain = 0.85;
  const endAt = now + 5.5;
  const master = createMasterGain(ctx, now, endAt, peakGain);
  [880, 988, 880, 784].forEach((freq, index) => {
    const t = now + index * 1.1;
    tone(ctx, t, 1.0, freq, "sine", 0.7, master);
    tone(ctx, t, 0.8, freq * 2.01, "sine", 0.25, master);
    tone(ctx, t, 0.6, freq * 3.02, "sine", 0.12, master);
  });
}

const PRESET_PLAYERS = {
  alarm: playAlarmPreset,
  siren: playSirenPreset,
  urgent: playUrgentPreset,
  bell: playBellPreset,
};

function playPresetSound(type) {
  const ctx = getAudioContext();
  if (!ctx) {
    return;
  }
  const soundType =
    type && PRESET_PLAYERS[type] ? type : currentSoundType || DEFAULT_PRESET_TYPE;
  const run = () => {
    PRESET_PLAYERS[soundType](ctx, ctx.currentTime);
  };
  if (ctx.state === "suspended") {
    ctx.resume().then(run).catch(() => {});
    return;
  }
  run();
}

function playCustomMp3() {
  const el = getCustomAudioEl();
  el.muted = false;
  el.volume = 1;
  try {
    el.pause();
    el.currentTime = 0;
  } catch (e) {}
  realPlayStartedAt = Date.now();
  return el.play();
}

/**
 * 播放报警声：优先 custom.mp3，否则内置 alarm（或指定 preset）
 * @param {string} [type] alarm | siren | urgent | bell
 */
export function playAlarmSound(type) {
  unlockAlarmSound();
  const fallback = (from) => {
    debugLog("fall back to preset:", from, "currentSoundType=" + currentSoundType);
    playPresetSound(type || currentSoundType || DEFAULT_PRESET_TYPE);
  };
  // 只要没被明确判定为不可用, 就总是先尝试自定义 mp3, 播放失败再回退
  ensureCustomMp3Checked().then((ok) => {
    debugLog("playAlarmSound enter, customAvailable=" + customMp3Available + " ok=" + ok);
    if (customMp3Available === false) {
      fallback("custom unavailable");
      return;
    }
    try {
      playCustomMp3().catch((err) => {
        fallback("mp3 play rejected: " + err.name + " - " + err.message);
      });
    } catch (e) {
      fallback("mp3 play throw: " + e.message);
    }
  });
}

/** 开启/关闭诊断日志: 控制台 __alarm.debug(true) 打开 */
export function setAlarmDebug(on) {
  window.__alarmDebug = !!on;
  console.log("[alarmSound] debug =", window.__alarmDebug);
}

/** 手动重新检测自定义 mp3(改了文件后用这个, 或控制台 __alarm.recheck()) */
export function recheckCustomMp3() {
  customMp3Probe = null;
  customMp3Available = false;
  return ensureCustomMp3Checked(true);
}

/**
 * 立即停止当前正在响的报警声(包括自定义 mp3 和内置 preset)
 * 常见用法: 点击 SOS"处理"按钮后调用
 */
export function stopAlarmSound() {
  // 1) 停自定义 mp3
  if (customAudioEl) {
    try {
      customAudioEl.pause();
      customAudioEl.currentTime = 0;
    } catch (e) {}
    try { customAudioEl.muted = false; } catch (e) {}
  }
  // 2) 停当前 preset 的振荡器 + 断开 master gain
  activeOscs.forEach((osc) => {
    try { osc.stop(); } catch (e) {}
    try { osc.disconnect(); } catch (e) {}
  });
  activeOscs = [];
  if (activeMaster && audioContext) {
    try {
      const t = audioContext.currentTime;
      activeMaster.gain.cancelScheduledValues(t);
      activeMaster.gain.setValueAtTime(0.0001, t);
      activeMaster.disconnect();
    } catch (e) {}
  }
  activeMaster = null;
  debugLog("stopAlarmSound called");
}

/** 预览所有内置音效（控制台调试用） */
export function previewAllAlarmSounds() {
  ALARM_SOUND_TYPES.forEach((type, index) => {
    setTimeout(() => playPresetSound(type), index * 7000);
  });
}
